import pick from 'lodash/pick';

import { sendTrackingEvent, TRACKING_EVENTS } from '../../analytics/trackingEvent';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import {
  getProcess,
  isBookingProcessAlias,
  isDigitalProductsProcessName,
} from '../../transactions/transaction';
import { transitions as instantBookingTransitions } from '../../transactions/transactionProcessInstantBooking';
import { transitions as payLaterTransitions } from '../../transactions/transactionProcessPayLaterBooking';
import {
  firstTimeslot as firstTimeslotApi,
  fetchHandleByUserIdApi,
  fetchListingContentAccess,
  initiatePrivileged,
  transactionLineItems,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { EMPTY_DIGITAL_LISTING_ACCESS } from '../../util/digitalListingAccess';
import { normalizePackages, siblingListingIds } from '../../util/listingPackages';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/ListingPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const FETCH_FIRST_TIMESLOT_REQUEST = 'app/ListingPage/FETCH_FIRST_TIMESLOT_REQUEST';
export const FETCH_FIRST_TIMESLOT_SUCCESS = 'app/ListingPage/FETCH_FIRST_TIMESLOT_SUCCESS';
export const FETCH_FIRST_TIMESLOT_ERROR = 'app/ListingPage/FETCH_FIRST_TIMESLOT_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const FETCH_USER_TRANSACTIONS_REQUEST = 'app/ListingPage/FETCH_USER_TRANSACTIONS_REQUEST';
export const FETCH_USER_TRANSACTIONS_SUCCESS = 'app/ListingPage/FETCH_USER_TRANSACTIONS_SUCCESS';
export const FETCH_USER_TRANSACTIONS_ERROR = 'app/ListingPage/FETCH_USER_TRANSACTIONS_ERROR';
export const FETCH_USER_TRANSACTIONS_ABORTED = 'app/ListingPage/FETCH_USER_TRANSACTIONS_ABORTED';

export const FETCH_DIGITAL_LISTING_ACCESS_REQUEST =
  'app/ListingPage/FETCH_DIGITAL_LISTING_ACCESS_REQUEST';
export const FETCH_DIGITAL_LISTING_ACCESS_SUCCESS =
  'app/ListingPage/FETCH_DIGITAL_LISTING_ACCESS_SUCCESS';
export const FETCH_DIGITAL_LISTING_ACCESS_ERROR =
  'app/ListingPage/FETCH_DIGITAL_LISTING_ACCESS_ERROR';
export const FETCH_DIGITAL_LISTING_ACCESS_ABORTED =
  'app/ListingPage/FETCH_DIGITAL_LISTING_ACCESS_ABORTED';

export const CANCEL_CURRENT_BOOKING_REQUEST = 'app/ListingPage/CANCEL_CURRENT_BOOKING_REQUEST';
export const CANCEL_CURRENT_BOOKING_SUCCESS = 'app/ListingPage/CANCEL_CURRENT_BOOKING_SUCCESS';
export const CANCEL_CURRENT_BOOKING_ERROR = 'app/ListingPage/CANCEL_CURRENT_BOOKING_ERROR';

export const INSTANT_ORDER_ERROR = 'app/ListingPage/INSTANT_ORDER_ERROR';
export const INSTANT_ORDER_SUCCESS = 'app/ListingPage/INSTANT_ORDER_SUCCESS';
export const SET_AUTHOR_HANDLE = 'app/ListingPage/SET_AUTHOR_HANDLE';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  showListingInProgress: false,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  firstTimeslot: null,
  fetchFirstTimeslotInProgress: false,
  fetchFirstTimeslotError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  fetchUserTransactionsInProgress: true,
  fetchUserTransactionsError: null,
  cancelCurrentBookingInProgress: false,
  cancelCurrentBookingError: null,
  userTransactions: [],
  instantOrderError: null,
  instantOrderSuccess: false,
  authorHandle: null,
  digitalListingAccess: EMPTY_DIGITAL_LISTING_ACCESS,
  fetchDigitalListingAccessInProgress: false,
  fetchDigitalListingAccessError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      // Clear listing-scoped side data here (not via SET_INITIAL_VALUES) so concurrent
      // navigations / StrictMode remounts cannot wipe a newer in-flight request.
      return {
        ...state,
        id: payload.id,
        showListingError: null,
        showListingInProgress: true,
        lineItems: null,
        fetchLineItemsError: null,
        fetchLineItemsInProgress: false,
        monthlyTimeSlots: {},
        reviews: [],
        fetchReviewsError: null,
        userTransactions: [],
        fetchUserTransactionsError: null,
        cancelCurrentBookingError: null,
        instantOrderError: null,
        instantOrderSuccess: false,
        authorHandle: null,
        digitalListingAccess: EMPTY_DIGITAL_LISTING_ACCESS,
        fetchDigitalListingAccessInProgress: false,
        fetchDigitalListingAccessError: null,
      };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload, showListingInProgress: false };
    case SHOW_LISTING_SUCCESS:
      return { ...state, showListingInProgress: false, showListingError: null };
    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case FETCH_FIRST_TIMESLOT_REQUEST:
      return {
        ...state,
        firstTimeslot: null,
        fetchFirstTimeslotInProgress: true,
        fetchFirstTimeslotError: null,
      };
    case FETCH_FIRST_TIMESLOT_SUCCESS:
      return {
        ...state,
        fetchFirstTimeslotInProgress: false,
        firstTimeslot: payload,
      };
    case FETCH_FIRST_TIMESLOT_ERROR:
      return {
        ...state,
        fetchFirstTimeslotInProgress: false,
        fetchFirstTimeslotError: payload,
      };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };
    case FETCH_USER_TRANSACTIONS_REQUEST:
      return { ...state, fetchUserTransactionsInProgress: true, fetchUserTransactionsError: null };
    case FETCH_USER_TRANSACTIONS_SUCCESS: {
      const data = payload.data;
      return { ...state, fetchUserTransactionsInProgress: false, userTransactions: data };
    }
    case FETCH_USER_TRANSACTIONS_ERROR:
      return {
        ...state,
        fetchUserTransactionsInProgress: false,
        fetchUserTransactionsError: payload,
      };
    case FETCH_USER_TRANSACTIONS_ABORTED:
      return { ...state, fetchUserTransactionsInProgress: false };

    case FETCH_DIGITAL_LISTING_ACCESS_REQUEST:
      return {
        ...state,
        fetchDigitalListingAccessInProgress: true,
        fetchDigitalListingAccessError: null,
      };
    case FETCH_DIGITAL_LISTING_ACCESS_SUCCESS:
      return {
        ...state,
        fetchDigitalListingAccessInProgress: false,
        digitalListingAccess: payload || EMPTY_DIGITAL_LISTING_ACCESS,
      };
    case FETCH_DIGITAL_LISTING_ACCESS_ERROR:
      return {
        ...state,
        fetchDigitalListingAccessInProgress: false,
        fetchDigitalListingAccessError: payload,
        digitalListingAccess: EMPTY_DIGITAL_LISTING_ACCESS,
      };
    case FETCH_DIGITAL_LISTING_ACCESS_ABORTED:
      return {
        ...state,
        fetchDigitalListingAccessInProgress: false,
        digitalListingAccess: EMPTY_DIGITAL_LISTING_ACCESS,
      };

    case CANCEL_CURRENT_BOOKING_REQUEST:
      return { ...state, cancelCurrentBookingInProgress: true, cancelCurrentBookingError: null };
    case CANCEL_CURRENT_BOOKING_SUCCESS:
      return { ...state, cancelCurrentBookingInProgress: false };
    case CANCEL_CURRENT_BOOKING_ERROR:
      return {
        ...state,
        cancelCurrentBookingInProgress: false,
        cancelCurrentBookingError: payload,
      };

    case INSTANT_ORDER_ERROR:
      return { ...state, instantOrderError: payload };
    case INSTANT_ORDER_SUCCESS:
      return { ...state, instantOrderSuccess: true };
    case SET_AUTHOR_HANDLE:
      return { ...state, authorHandle: payload || null };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingSuccess = () => ({
  type: SHOW_LISTING_SUCCESS,
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const fetchFirstTimeslotRequest = () => ({ type: FETCH_FIRST_TIMESLOT_REQUEST });
export const fetchFirstTimeslotSuccess = timeslot => ({
  type: FETCH_FIRST_TIMESLOT_SUCCESS,
  payload: timeslot,
});
export const fetchFirstTimeslotError = error => ({
  type: FETCH_FIRST_TIMESLOT_ERROR,
  error: true,
  payload: error,
});

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

export const fetchUserTransactionsAborted = () => ({ type: FETCH_USER_TRANSACTIONS_ABORTED });
export const fetchUserTransactionsRequest = () => ({ type: FETCH_USER_TRANSACTIONS_REQUEST });
export const fetchUserTransactionsSuccess = userTransactions => ({
  type: FETCH_USER_TRANSACTIONS_SUCCESS,
  payload: userTransactions,
});
export const fetchUserTransactionsError = e => ({
  type: FETCH_USER_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

export const fetchDigitalListingAccessRequest = () => ({
  type: FETCH_DIGITAL_LISTING_ACCESS_REQUEST,
});
export const fetchDigitalListingAccessSuccess = access => ({
  type: FETCH_DIGITAL_LISTING_ACCESS_SUCCESS,
  payload: access,
});
export const fetchDigitalListingAccessError = e => ({
  type: FETCH_DIGITAL_LISTING_ACCESS_ERROR,
  error: true,
  payload: e,
});
export const fetchDigitalListingAccessAborted = () => ({
  type: FETCH_DIGITAL_LISTING_ACCESS_ABORTED,
});

export const cancelCurrentBookingRequest = () => ({ type: CANCEL_CURRENT_BOOKING_REQUEST });
export const cancelCurrentBookingSuccess = () => ({ type: CANCEL_CURRENT_BOOKING_SUCCESS });
export const cancelCurrentBookingError = e => ({
  type: CANCEL_CURRENT_BOOKING_ERROR,
  error: true,
  payload: e,
});

const instantOrderError = e => ({
  type: INSTANT_ORDER_ERROR,
  error: true,
  payload: e,
});

export const instantOrderSuccess = order => ({
  type: INSTANT_ORDER_SUCCESS,
  payload: order,
});

export const setAuthorHandle = handle => ({
  type: SET_AUTHOR_HANDLE,
  payload: handle || null,
});

const authorIdFromShowResponse = data => {
  const listing = data?.data?.data;
  const relId = listing?.relationships?.author?.data?.id;
  if (relId) {
    return relId.uuid || relId;
  }
  const included = data?.data?.included || [];
  const author = included.find(entity => entity.type === 'user' || entity.type === 'currentUser');
  return author?.id?.uuid || null;
};

const resolveAuthorHandle = listingResponse => dispatch => {
  const authorId = authorIdFromShowResponse(listingResponse);
  if (!authorId) {
    return Promise.resolve(null);
  }
  return fetchHandleByUserIdApi(authorId)
    .then(result => {
      if (result?.data?.handle) {
        dispatch(setAuthorHandle(result.data.handle));
      }
      return result;
    })
    .catch(() => null);
};

// ================ Thunks ================ //

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  const requestedId = listingId?.uuid || listingId;

  const isCurrentRequest = () => {
    const currentId = getState()?.ListingPage?.id;
    const current = currentId?.uuid || currentId;
    return current && requestedId && String(current) === String(requestedId);
  };

  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.reviewStats',
      // Sparse author payload must include handle so listing → profile links
      // use /u/:handle instead of falling back to the UUID.
      'profile.publicData.handle',
    ],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media (Facebook images work perfectly for WhatsApp)
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      // Always merge entities; only mark success for the active request (avoids race flicker).
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      if (isCurrentRequest()) {
        dispatch(showListingSuccess());
        dispatch(resolveAuthorHandle(data));
      }
      return data;
    })
    .catch(e => {
      // Ignore stale failures from a previous listing navigation / StrictMode double-fetch.
      if (isCurrentRequest()) {
        dispatch(showListingError(storableError(e)));
      }
      // Never rethrow plain objects (CRA overlay shows "[object Object]").
      return null;
    });
};

export const fetchPackageSiblings = (listing, config) => (dispatch, getState, sdk) => {
  const pkgs = normalizePackages(listing?.attributes?.publicData?.packages);
  const ids = siblingListingIds(pkgs?.items, listing?.id?.uuid);
  if (!ids.length) {
    return Promise.resolve(null);
  }

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      ids: ids.join(','),
      perPage: ids.length,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData.reviewStats',
        'profile.publicData.handle',
      ],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,
        'variants.facebook',
        'variants.twitter',
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    })
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(data, { listingFields }));
      return data;
    })
    .catch(e => {
      log.error(e, 'fetch-package-siblings-failed', { ids });
      return null;
    });
};

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      listing_id: listingId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

// Helper function for loadData call.
export const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const unitType = attributes?.publicData?.unitType;
    const timeUnit = unitType === 'hour' ? 'hour' : 'day';
    const nextBoundary = findNextBoundary(new Date(), timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId?.uuid,
        orderData,
      });
      // Soft-fail: line-item estimate is secondary to date selection.
      return null;
    });
};

export const fetchFirstTimeslot = ({ listingId, timeslotStart, timeslotEnd }) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchFirstTimeslotRequest());

  const finishWithTimeslot = timeslot => {
    dispatch(fetchFirstTimeslotSuccess(timeslot));
    if (!timeslot) {
      sendTrackingEvent({
        name: TRACKING_EVENTS.NO_FIRST_TIMESLOTS,
        parameters: {
          listingId: listingId?.uuid,
        },
      });
    }
    return timeslot;
  };

  /** When local API (:3500) is down, still resolve bookable slots via Marketplace SDK. */
  const fallbackViaSdk = () => {
    const start = timeslotStart && timeslotEnd ? new Date(timeslotStart) : new Date();
    const end =
      timeslotStart && timeslotEnd
        ? new Date(timeslotEnd)
        : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return Promise.resolve(null);
    }
    return sdk.timeslots
      .query({
        listingId,
        start,
        end,
        perPage: 1,
        page: 1,
      })
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        return entities?.[0] || null;
      });
  };

  return firstTimeslotApi({ listingId, timeslotStart, timeslotEnd })
    .then(response => finishWithTimeslot(response?.data ?? null))
    .catch(e => {
      return fallbackViaSdk()
        .then(timeslot => finishWithTimeslot(timeslot))
        .catch(() => {
          const err = storableError(e);
          dispatch(fetchFirstTimeslotError(err));
          // Soft-fail: do not throw storableError objects (CRA overlay shows "[object Object]").
          return null;
        });
    });
};

const parseTimeslotParamsFromSearch = search => {
  if (!search || typeof search !== 'string') return {};
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const start = params.get('timeslotStart');
  const end = params.get('timeslotEnd');
  return start && end ? { timeslotStart: start, timeslotEnd: end } : {};
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const listingId = new UUID(params.id);
  const timeslotParams = parseTimeslotParamsFromSearch(search);

  // Do NOT call setInitialValues here — it resets to initialState and races with
  // in-flight showListing (React StrictMode double-mount / fast home→listing clicks),
  // which previously left a stale showListingError and "Could not load the listing".
  // SHOW_LISTING_REQUEST clears listing-scoped side data safely.

  // Fetch first available timeslot (or specific one if timeslotStart/End in URL). Soft-fail.
  dispatch(fetchFirstTimeslot({ listingId, ...timeslotParams })).catch(() => null);

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  return Promise.all([
    dispatch(showListing(listingId, config)),
    dispatch(fetchReviews(listingId)).catch(() => null),
  ])
    .then(response => {
      const listing = response?.[0]?.data?.data;
      if (!listing) {
        // showListing already dispatched showListingError (or returned null); avoid TypeError.
        return response;
      }
      const transactionProcessAlias =
        listing?.attributes?.publicData?.transactionProcessAlias || '';
      if (isBookingProcessAlias(transactionProcessAlias)) {
        // Fetch timeSlots.
        // This can happen parallel to loadData.
        // We are not interested to return them from loadData call.
        fetchMonthlyTimeSlots(dispatch, listing);
        dispatch(fetchUserTransactions({ listingId }));
      } else if (isDigitalProductsProcessName(transactionProcessAlias)) {
        const authorRel = listing?.relationships?.author?.data?.id;
        const authorId = authorRel?.uuid || authorRel;
        dispatch(fetchDigitalListingAccess({ listingId, authorId }));
      }
      dispatch(fetchPackageSiblings(listing, config)).catch(() => null);
      return response;
    })
    .catch(e => {
      // Soft-fail loadData so Routes.js never gets a thrown storableError object.
      const pageState = getState()?.ListingPage;
      const current = pageState?.id?.uuid || pageState?.id;
      if (current && String(current) === String(listingId.uuid) && !pageState?.showListingError) {
        const err =
          e instanceof Error ? e : new Error((e && e.message) || 'Failed to load listing');
        dispatch(showListingError(storableError(err)));
      }
      return null;
    });
};

export const fetchUserTransactions = ({ listingId }) => (dispatch, getState, sdk) => {
  const { auth } = getState();
  if (!auth.isAuthenticated) {
    dispatch(fetchUserTransactionsAborted());
    return;
  }

  dispatch(fetchUserTransactionsRequest());

  // This should represent last transitions before booked for both with/without payments
  const lastTransitions = [
    payLaterTransitions.REQUEST_BOOKING,
    payLaterTransitions.CUSTOMER_CANCEL,
    instantBookingTransitions.CONFIRM_PAYMENT,
  ];

  return sdk.transactions
    .query({
      only: 'order',
      lastTransitions,
      listingId,
    })
    .then(response => {
      const userTransactions = response.data;
      dispatch(fetchUserTransactionsSuccess(userTransactions));
    })
    .catch(e => {
      dispatch(fetchUserTransactionsError(storableError(e)));
      log.error(e, 'fetch-user-transactions-failed', {
        listingId,
        lastTransitions,
      });
    });
};

export const fetchDigitalListingAccess = ({ listingId, authorId } = {}) => (dispatch, getState) => {
  const { auth } = getState();
  if (!auth.isAuthenticated) {
    dispatch(fetchDigitalListingAccessAborted());
    return Promise.resolve();
  }

  const id = listingId?.uuid || listingId;
  if (!id) {
    dispatch(fetchDigitalListingAccessAborted());
    return Promise.resolve();
  }

  const run = () => {
    const currentUser = getState()?.user?.currentUser;
    const currentId = currentUser?.id?.uuid;
    if (authorId && currentId && String(currentId) === String(authorId)) {
      dispatch(fetchDigitalListingAccessAborted());
      return Promise.resolve();
    }

    dispatch(fetchDigitalListingAccessRequest());
    return fetchListingContentAccess(id)
      .then(response => {
        const data = response?.data || EMPTY_DIGITAL_LISTING_ACCESS;
        const status = data.status || EMPTY_DIGITAL_LISTING_ACCESS.status;
        const transactionId = data.transactionId || null;
        const expiredItemIds = Array.isArray(data.expiredItemIds) ? data.expiredItemIds : [];
        const trialEligible = data.trialEligible !== false;
        if (status === 'active' && !transactionId) {
          dispatch(fetchDigitalListingAccessSuccess(EMPTY_DIGITAL_LISTING_ACCESS));
          return EMPTY_DIGITAL_LISTING_ACCESS;
        }
        const access = { status, transactionId, expiredItemIds, trialEligible };
        dispatch(fetchDigitalListingAccessSuccess(access));
        return access;
      })
      .catch(e => {
        dispatch(fetchDigitalListingAccessError(storableError(e)));
        log.error(e, 'fetch-digital-listing-access-failed', { listingId: id });
      });
  };

  if (!getState()?.user?.currentUser) {
    return Promise.resolve(dispatch(fetchCurrentUser()))
      .then(run)
      .catch(run);
  }
  return run();
};

export const cancelCurrentBooking = ({ transactionId, listingId }) => (dispatch, getState, sdk) => {
  const { auth } = getState();
  if (!auth.isAuthenticated) {
    return;
  }
  dispatch(cancelCurrentBookingRequest());

  return sdk.transactions
    .transition({
      id: transactionId,
      transition: payLaterTransitions.CUSTOMER_CANCEL,
      params: {},
    })
    .then(() => {
      dispatch(cancelCurrentBookingSuccess());
      dispatch(fetchUserTransactions({ listingId }));
    })
    .catch(e => {
      dispatch(cancelCurrentBookingError(storableError(e)));
      log.error(e, 'cancel-current-transaction-failed', {
        transactionId,
      });
    });
};

export const initiateInstantOrder = ({ orderParams, processAlias, transitionName }) => (
  dispatch,
  getState,
  sdk
) => {
  // this is to simulate early request button inProgress
  dispatch(fetchUserTransactionsRequest());

  const { bookingDates, listingId, protectedData, quantity, tierId } = orderParams;
  const transitionParams = {
    ...bookingDates,
    listingId,
    protectedData,
  };

  const bodyParams = {
    processAlias,
    transition: transitionName,
    params: transitionParams,
  };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(fetchUserTransactions({ listingId }));
    dispatch(instantOrderSuccess({ order }));
    return order;
  };

  const handleError = e => {
    const stored = storableError(e);
    dispatch(instantOrderError(stored));
    log.error(e, 'initiate-instant-order-failed', {
      listingId: orderParams?.listingId?.uuid,
      ...protectedData,
    });
    // Soft-fail: do not rethrow — CRA overlay shows "Script error." / "[object Object]"
    // for API rejection objects and breaks the booking date-change UX.
    return null;
  };

  const orderData = {
    ...(tierId ? { tierId } : {}),
  };

  return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
    .then(handleSucces)
    .catch(handleError);
};
